/** UploadDocumentModal — enhanced upload modal with document type, folder, progress, drag-drop. */

import { useState, useCallback, useMemo } from "react";
import { Modal, Input, Select, Upload, Progress, App, theme } from "antd";
import {
  UploadOutlined,
  InboxOutlined,
  FileTextOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { useUploadDocument, useMatterTree } from "../../api/queries";
import { DOCUMENT_TYPE_OPTIONS, DOCUMENT_TYPE_FOLDER_LABELS } from "../../constants";
import type { DocumentType } from "../../constants";
import {
  formatFileSize,
  buildUploadNotices,
  settleUploadOutcomes,
} from "../../utils";
import type { FileUploadOutcome } from "../../utils";
import styles from "../../styles/upload-modal.module.css";
import icon from "../../styles/icon.module.css";

const { Dragger } = Upload;

/** Per-file upload status tracking. */
interface FileUploadState {
  uid: string;
  file: File;
  name: string;
  size: number;
  status: "pending" | "uploading" | "success" | "error";
  percent: number;
  errorMsg?: string;
}

interface UploadDocumentModalProps {
  open: boolean;
  onClose: () => void;
  matterId: string;
  /** Pre-selected folder from drag-drop or tree click. */
  defaultFolder?: string;
  /** Files pre-loaded from drag-drop. */
  defaultFiles?: File[];
}

export default function UploadDocumentModal({
  open,
  onClose,
  matterId,
  defaultFolder,
  defaultFiles,
}: UploadDocumentModalProps) {
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const [title, setTitle] = useState("");
  const [docType, setDocType] = useState<DocumentType>("contract");
  const [folder, setFolder] = useState(defaultFolder ?? "contract");
  const [fileStates, setFileStates] = useState<FileUploadState[]>(() =>
    defaultFiles
      ? defaultFiles.map((f) => ({
          uid: `${f.name}-${f.size}-${Date.now()}`,
          file: f,
          name: f.name,
          size: f.size,
          status: "pending" as const,
          percent: 0,
        }))
      : [],
  );
  const [uploading, setUploading] = useState(false);
  const uploadDoc = useUploadDocument();

  // Fetch matter tree to get available folders
  const { data: treeDocuments } = useMatterTree(matterId);

  // Build folder options from tree data + constant labels
  const folderOptions = useMemo(() => {
    const typesInTree = new Set<string>();
    if (treeDocuments) {
      for (const doc of treeDocuments) {
        typesInTree.add(doc.type ?? "other");
      }
    }

    // Merge tree types with standard labels
    const options: { value: string; label: string }[] = [];
    const seen = new Set<string>();

    for (const type of typesInTree) {
      const label = DOCUMENT_TYPE_FOLDER_LABELS[type] ?? type;
      options.push({ value: type, label });
      seen.add(type);
    }

    // Add remaining standard folders not in tree
    for (const [type, label] of Object.entries(DOCUMENT_TYPE_FOLDER_LABELS)) {
      if (!seen.has(type)) {
        options.push({ value: type, label });
      }
    }

    return options;
  }, [treeDocuments]);

  // Add files from the Dragger
  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

  const handleBeforeUpload = useCallback(
    (_file: File, fileList: File[]) => {
      const validFiles = fileList.filter((f) => {
        if (f.size > MAX_FILE_SIZE) {
          message.error(`${f.name} 超过 50MB 限制`);
          return false;
        }
        return true;
      });

      if (validFiles.length === 0) return false;

      const newStates: FileUploadState[] = validFiles.map((f) => ({
        uid: `${f.name}-${f.size}-${Date.now()}`,
        file: f,
        name: f.name,
        size: f.size,
        status: "pending" as const,
        percent: 0,
      }));

      setFileStates((prev) => {
        const existing = prev.filter((s) => s.status !== "pending");
        return [...existing, ...newStates];
      });

      return false;
    },
    [],
  );

  const handleRemoveFile = useCallback((uid: string) => {
    setFileStates((prev) => prev.filter((s) => s.uid !== uid));
  }, []);

  const handleUpload = useCallback(async () => {
    const pendingFiles = fileStates.filter((s) => s.status === "pending");
    if (pendingFiles.length === 0) {
      message.warning("请选择文件");
      return;
    }

    setUploading(true);

    const results = await Promise.allSettled(
      pendingFiles.map(async (fs): Promise<FileUploadOutcome> => {
        setFileStates((prev) =>
          prev.map((s) =>
            s.uid === fs.uid ? { ...s, status: "uploading", percent: 30 } : s,
          ),
        );

        try {
          const res = await uploadDoc.mutateAsync({
            matterId,
            title: title.trim() || fs.name,
            type: docType,
            file: fs.file,
          });

          setFileStates((prev) =>
            prev.map((s) =>
              s.uid === fs.uid ? { ...s, status: "success", percent: 100 } : s,
            ),
          );
          return { saved: true, ragIndexStatus: res.data.ragIndexStatus };
        } catch {
          setFileStates((prev) =>
            prev.map((s) =>
              s.uid === fs.uid
                ? { ...s, status: "error", percent: 0, errorMsg: "上传失败，请检查文件格式和网络" }
                : s,
            ),
          );
          return { saved: false };
        }
      }),
    );

    setUploading(false);

    const notices = buildUploadNotices(settleUploadOutcomes(results));
    if (notices.success) message.success(notices.success);
    if (notices.error) message.error(notices.error);
    if (notices.indexWarning) message.warning(notices.indexWarning);

    if (notices.failedCount === 0) {
      setTimeout(() => {
        handleClose();
      }, 800);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileStates, title, docType, matterId, uploadDoc]);

  const handleClose = useCallback(() => {
    if (uploading) return;
    setTitle("");
    setDocType("contract");
    setFolder(defaultFolder ?? "contract");
    setFileStates([]);
    onClose();
  }, [uploading, defaultFolder, onClose]);

  const hasFiles = fileStates.length > 0;
  const allSuccess = hasFiles && fileStates.every((s) => s.status === "success");
  const hasErrors = fileStates.some((s) => s.status === "error");

  const renderStatusIcon = (status: FileUploadState["status"]) => {
    switch (status) {
      case "success":
        return <CheckCircleOutlined className={styles.fileItemStatusSuccess} />;
      case "error":
        return <CloseCircleOutlined className={styles.fileItemStatusError} />;
      case "uploading":
        return <LoadingOutlined className={styles.fileItemStatusUploading} />;
      default:
        return null;
    }
  };

  return (
    <Modal
      title="上传文档"
      open={open}
      onOk={allSuccess ? handleClose : handleUpload}
      onCancel={handleClose}
      confirmLoading={uploading}
      okText={allSuccess ? "完成" : "上传"}
      cancelText="取消"
      destroyOnHidden
      width={560}
      rootClassName="jerryUploadDocModal"
      okButtonProps={{
        disabled: (!hasFiles && !allSuccess) || (hasErrors && !uploading),
      }}
    >
      <div className={styles.formSection}>
        {/* Title */}
        <div className={styles.formRow}>
          <label className={styles.formLabel}>文档标题</label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="不填则使用文件名"
          />
        </div>

        {/* Type + Folder Row */}
        <div className={styles.typeSelectRow}>
          <div className={styles.formRow}>
            <label className={styles.formLabel}>文档类型</label>
            <Select
              value={docType}
              onChange={(v: DocumentType) => {
                setDocType(v);
                if (folder === "contract" || folder === docType) {
                  setFolder(v);
                }
              }}
              options={DOCUMENT_TYPE_OPTIONS}
            />
          </div>
          <div className={styles.formRow}>
            <label className={styles.formLabel}>目标文件夹</label>
            <Select
              value={folder}
              onChange={setFolder}
              options={folderOptions}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </div>
        </div>

        {/* Drag & Drop Zone */}
        {!hasFiles && (
          <Dragger
            multiple
            showUploadList={false}
            beforeUpload={handleBeforeUpload}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.ofd,.rar,.zip"
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined className={icon.xxlMuted} />
            </p>
            <p className={styles.dropText}>
              拖拽文件到此处，或 <span className={styles.dropBrowseLink}>浏览文件</span>
            </p>
            <p className={styles.dropHint}>
              支持 PDF、Word、Excel、PPT、TXT、图片、OFD、压缩包，单个文件不超过 50MB
            </p>
          </Dragger>
        )}

        {/* File List with Progress */}
        {hasFiles && (
          <div className={styles.fileListSection}>
            {fileStates.map((fs) => (
              <div key={fs.uid} className={styles.fileItemRow}>
                <span className={styles.fileItemIcon}>
                  <FileTextOutlined />
                </span>
                <div className={styles.fileItemInfo}>
                  <div className={styles.fileItemName}>{fs.name}</div>
                  <div className={styles.fileItemMeta}>
                    <span>{formatFileSize(fs.size)}</span>
                    <span>{DOCUMENT_TYPE_OPTIONS.find((o) => o.value === docType)?.label}</span>
                  </div>
                  {fs.status === "uploading" && (
                    <Progress
                      percent={fs.percent}
                      size="small"
                      className={styles.fileItemProgress}
                      strokeColor={token.colorPrimary}
                      showInfo={false}
                    />
                  )}
                </div>
                <span className={styles.fileItemStatus}>
                  {renderStatusIcon(fs.status)}
                  {fs.status === "error" && (
                    <span className={styles.fileItemStatusError}>{fs.errorMsg}</span>
                  )}
                </span>
                {fs.status === "pending" && (
                  <button
                    type="button"
                    className={styles.fileItemRemove}
                    onClick={() => handleRemoveFile(fs.uid)}
                    aria-label="移除文件"
                  >
                    <DeleteOutlined />
                  </button>
                )}
              </div>
            ))}

            {/* Add more files */}
            {!allSuccess && (
              <Upload
                multiple
                showUploadList={false}
                beforeUpload={handleBeforeUpload}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.ofd,.rar,.zip"
              >
                <button
                  type="button"
                  className={styles.addMoreBtn}
                >
                  <UploadOutlined /> 添加更多文件
                </button>
              </Upload>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
